import { PureComponent } from 'react'
import _ from 'lodash'

import cx from 'classnames'
import PropTypes from 'prop-types'
import { PRODUCT_SHIP_TYPE, PROPOSAL_TYPE, FIELD_MAX_NUM_VAL } from '@vzmi/shp_shared_compo/constants/proposal'
import { getFeatureToggle } from '@vzmi/component_ec_nodejs_util/dist/lib/FeatureToggleUtil'
import styles from './Limitation.css'
import {
  FormRow,
  When,
  InputField,
  InputCountField,
  CheckboxInputField
} from '../../../../Common/Form'
import { Field } from '../../../../Common/forms'
import SingleLineText from '../../../Common/ReadOnlyComponents/SingleLineText/SingleLineText'
import withPrice from '../../../Common/ReadOnlyComponents/HOC/withPrice'
import {
  IconPopoverLabel,
  LimitDescBtwCostAndStock
} from '../../../Common/ProposalForm/FormLabelTooltip/FormLabelTooltip'
import { isHomeStore, getPriceCap, getCvsQtyLimitMaxByPrice } from '../../../Common/ProposalForm/Shared/util'
import { parsePriceWithFixedDigits } from '../../../../../lib/parser'
import { applyLowGpmValidation } from '../../../Common/ProposalForm/Shared/validation/validations'
import { atLeast } from '../../../Common/ProposalForm/Shared/normalizers'
import { ListingFieldConfig } from '../../../Common/ProposalForm/Shared/FieldConfig'
import { isNewListingProposal, isNewProductProposal } from '../../../../../../lib/proposalHelper'
import {
  formatAll,
  zeroOrNegativeFormatTo,
  showOnlyNumber,
  showNoLessThan,
  showLessThan,
  showOnlyPositiveNumber,
  showRoundedNumber
} from '../../../../../lib/formatter'

// APACSMDESK-47948 移除申請低毛, 目前用開關控制
//  TODO: 真正要完全移除開關時, 搜尋"applyLowGpm"可以找到所有相關的code
const getGpmFeatureToggle = flags => getFeatureToggle(flags, 'sp_gpm')

// 安全庫存＆單次限購(宅配)format
const resetSafeStackQty = (userPurchaseQty) => {
  return formatAll(
    showOnlyNumber,
    showRoundedNumber,
    zeroOrNegativeFormatTo(userPurchaseQty),
    showNoLessThan(1),
    showLessThan(FIELD_MAX_NUM_VAL.PURCHASE_QTY_LIMIT)
  )
}

// 單次限購(超取) formatter
const cvsPurchaseQtyLimitFormatter = cvsQtyLimitMax => {
  return formatAll(
    showOnlyPositiveNumber,
    showRoundedNumber,
    zeroOrNegativeFormatTo(cvsQtyLimitMax),
    showNoLessThan(1),
    showLessThan(cvsQtyLimitMax)
  )
}

const getSafeStockQtyFormatter = userSafeStockQty => {
  return formatAll(
    showOnlyNumber,
    showRoundedNumber,
    zeroOrNegativeFormatTo(userSafeStockQty),
    showNoLessThan(1),
    showLessThan(FIELD_MAX_NUM_VAL.SAFE_STOCK_QTY)
  )
}

/**
   * 獲得成本售價市價在不同  shipTypeId 下的 formatter。
   * newProduct 可以出現 0，
   * newListing 不可以
   * */
export const getAmountFormatter = (proposalType = '', shipTypeId = '', readOnly = false, amountType = '') => {
  const isNewListing = isNewListingProposal(proposalType)

  const priceCap = getPriceCap(shipTypeId, amountType)
  const showPriceUnderCap = showLessThan(priceCap)

  let activeFormatters = isNewListing
    ? [
      showOnlyNumber,
      showOnlyPositiveNumber,
      showRoundedNumber,
      showPriceUnderCap
    ]
    : [
      showOnlyNumber,
      showRoundedNumber,
      showPriceUnderCap
    ]
  if (readOnly) {
    activeFormatters = activeFormatters.filter(
      fn => !_.includes([showOnlyPositiveNumber, showPriceUnderCap], fn)
    )
  }

  return formatAll(...activeFormatters)
}

class Limitation extends PureComponent {
  constructor (props) {
    super(props)
    const { shipTypeId = PRODUCT_SHIP_TYPE.HOME } = props
    this.state = {
      cvsPurchaseQtyLimitToggle: isHomeStore(shipTypeId),
      userPurchaseQty: null,
      userSafeStockQty: null
    }
  }

  /** 若調整售價，則連動限購數量為為最高可供購買數量 */
  handlePriceChange = (e, value) => {
    const { triggerFieldChange } = this.props
    const cvsPurchaseQtyLimitMax = getCvsQtyLimitMaxByPrice(value)
    triggerFieldChange(
      'listing.cvsPurchaseQtyLimit',
      cvsPurchaseQtyLimitMax
    )
  }

  changeUserCvsPurchaseQtyLimitTo = value => {
    const { triggerFieldChange } = this.props
    triggerFieldChange('setting.userCvsPurchaseQtyLimit', value)
  }

  componentDidUpdate (prevProps) {
    const {
      triggerFieldChange,
      proposalType,
      user: { identity: { isPM } }
    } = prevProps

    if (isNewListingProposal(proposalType)) {
      /** 配送方式更新 */
      const nextShipTypeId = _.get(this.props, 'shipTypeId', PRODUCT_SHIP_TYPE.HOME)
      const nextIsHomeStore = isHomeStore(nextShipTypeId)
      const { cvsPurchaseQtyLimitToggle } = this.state
      if (cvsPurchaseQtyLimitToggle !== nextIsHomeStore) {
        this.setState(() => ({
          // 當配送方式選擇直店配，則出現 超商取貨限購數量 選項
          cvsPurchaseQtyLimitToggle: nextIsHomeStore
        }))
      }

      // 是否申請低毛利率賣場，僅有"PM仿供應商登入"可填寫
      // 若是 PM 且賣場毛利率 < 子站毛利率，則需要 "申請低毛利率賣場"， show checkbox，但不自動勾選
      // 若是 PM 且 !(賣場毛利率 < 子站毛利率) ，hide checkbox, set applyLowGpm to false
      const nextUnderSubStationGpm = _.get(this.props, 'underSubStationGpm', false)
      const nextGpmFeatureToggle = getGpmFeatureToggle(_.get(this.props, 'flags', {}))
      const nextShouldApplyLowGpm = nextGpmFeatureToggle && isPM && nextUnderSubStationGpm
      const nextApplyLowGpm = _.get(this.props, 'applyLowGpm', false)
      if (!nextShouldApplyLowGpm && nextApplyLowGpm) {
        triggerFieldChange('listing.applyLowGpm', false)
      }
    }
  }

  // handle 安全庫存/單次限購(宅配)
  handleOnchange = (fieldName) => (e, newValue) => {
    const {
      userPurchaseQty,
      userSafeStockQty
    } = this.state
    switch (fieldName) {
      case 'safeStockQty': {
        const formatSafeStackQtyValue = resetSafeStackQty(userSafeStockQty)(newValue)
        if (userSafeStockQty !== formatSafeStackQtyValue && Number(formatSafeStackQtyValue)) {
          this.setState(() => ({ userSafeStockQty: formatSafeStackQtyValue }))
        }
        break
      }
      case 'purchaseQtyLimit': {
        const formatPurchaseQtyValue = resetSafeStackQty(userPurchaseQty)(newValue)
        if (userPurchaseQty !== formatPurchaseQtyValue && Number(formatPurchaseQtyValue)) {
          this.setState(() => ({ userPurchaseQty: formatPurchaseQtyValue }))
        }
        break
      }
      default:
        break
    }
  }

  /**
   * 安全庫存量在 newProduct 且快速到貨的狀況下，必須為 1 ，且唯讀
   */
  getSafeStockQtyCompo = () => {
    const {
      shipTypeId,
      proposalType,
      readOnly
    } = this.props

    const isNewProduct = isNewProductProposal(proposalType)
    const isExpress24HR = shipTypeId === PRODUCT_SHIP_TYPE.EXPRESS_24HR
    return (isNewProduct && isExpress24HR) || readOnly
      ? SingleLineText
      : InputField
  }

  render () {
    const {
      cvsPurchaseQtyLimitToggle,
      userPurchaseQty,
      userSafeStockQty
    } = this.state
    const {
      user: { identity: { isPM } },
      shipTypeId: originShipTypeId,
      readOnly,
      subStationGpm,
      listingGpm,
      underSubStationGpm,
      cvsQtyLimitMax,
      proposalType,
      mustShowFormError,
      flags,
      isNewProposal
    } = this.props
    const shipTypeId = +originShipTypeId

    const isNewListing = isNewListingProposal(proposalType)
    const gpmFeatureToggle = getGpmFeatureToggle(flags)
    const shouldApplyLowGpm = gpmFeatureToggle && isPM && underSubStationGpm

    const amountFormatter = getAmountFormatter(proposalType, shipTypeId, readOnly)
    const msrpFormatter = getAmountFormatter(proposalType, shipTypeId, readOnly, 'msrp')

    return (
      <div
        className={styles.Limitation}
        data-role='limitation'
      >
        <FormRow
          key='msrp'
          label='市價'
        >
          <Field
            name='product.msrp'
            data-role='product-msrp'
            className={styles.msrp}
            parse={parsePriceWithFixedDigits}
            format={msrpFormatter}
            component={
              readOnly
                ? withPrice(SingleLineText)
                : InputField
            }
            showErrorOnActive={isNewProposal}
            mustShowFormError={mustShowFormError}
          />
        </FormRow>
        <When
          value={isNewListing}
          isTrue
        >
          <FormRow
            key='price'
            label='購物中心售價'
          >
            <Field
              name='listing.price'
              data-role='listing-price'
              className={cx(styles.price, readOnly && styles.highlight)}
              component={
                readOnly
                  ? withPrice(SingleLineText)
                  : InputField
              }
              showErrorOnActive={isNewProposal}
              mustShowFormError={mustShowFormError}
              parse={parsePriceWithFixedDigits}
              format={amountFormatter}
              onChange={this.handlePriceChange}
            />
          </FormRow>
        </When>
        <FormRow
          key='cost'
          label={(
            <IconPopoverLabel
              popoverContent={(
                <>
                  進貨成本為購物中心應結款之金額，
                  <br />
                  填寫時請留意成本與數量限制：
                  <br />
                  <br />
                  <LimitDescBtwCostAndStock />
                </>
              )}
            >
              進貨成本
            </IconPopoverLabel>
          )}
        >
          <Field
            name='product.cost'
            data-role='product-cost'
            className={cx(styles.cost, readOnly && styles.highlight)}
            component={
              readOnly
                ? withPrice(SingleLineText)
                : InputField
            }
            parse={parsePriceWithFixedDigits}
            showErrorOnActive={isNewProposal}
            mustShowFormError={mustShowFormError}
            format={amountFormatter}
          />
        </FormRow>
        <When
          value={isNewListing}
          isTrue
        >
          <When
            value={isPM}
            isTrue
          >
            <FormRow
              key='gpm'
              label='毛利率'
              data-role='sub-station-gpm'
            >
              <SingleLineText
                content={subStationGpm}
              />
            </FormRow>
          </When>
          <FormRow
            key='listingGpm'
            label='賣場毛利率'
            data-role='listing-gpm'
          >
            <When
              value={_.isNil(listingGpm)}
              isFalse
            >
              <SingleLineText
                content={listingGpm}
              />
            </When>
            <When
              value={shouldApplyLowGpm}
              isTrue
            >
              <Field
                key='applyLowGpm'
                name='listing.applyLowGpm'
                data-role='apply-low-gpm'
                className={styles.applyLowGpm}
                component={CheckboxInputField}
                label={'申請低毛利率賣場'}
                validate={applyLowGpmValidation(shouldApplyLowGpm)}
                disabled={readOnly}
                showErrorOnActive={isNewProposal}
                mustShowFormError={mustShowFormError}
              />
            </When>
          </FormRow>
        </When>
        <FormRow
          key='safeStockQty'
          label='安全庫存量'
        >
          <Field
            name='product.safeStockQty'
            data-role='product-safe-stock-qty'
            className={styles.safeStockQty}
            component={this.getSafeStockQtyCompo()}
            onChange={this.handleOnchange('safeStockQty')}
            format={getSafeStockQtyFormatter(userSafeStockQty)}
            normalizeOnBlur={atLeast(1)}
            showErrorOnActive={isNewProposal}
            mustShowFormError={mustShowFormError}
          />
        </FormRow>
        <When
          value={isNewListing}
          isTrue
        >
          <FormRow
            key='purchaseQtyLimit'
            label='單次限購數量'
            subTitle='(宅配)'
            subTitleColor='blue'
          >
            <Field
              name='listing.purchaseQtyLimit'
              data-role='listing-purchase-qty-limit'
              className={styles.safeStockQty}
              component={
                readOnly
                  ? SingleLineText
                  : InputField
              }
              onChange={this.handleOnchange('purchaseQtyLimit')}
              format={resetSafeStackQty(userPurchaseQty)}
              normalizeOnBlur={atLeast(12)}
              showErrorOnActive={isNewProposal}
              mustShowFormError={mustShowFormError}
            />
          </FormRow>
          {cvsPurchaseQtyLimitToggle && (
            <FormRow
              key='cvsPurchaseQtyLimit'
              subTitle='(超取)'
              subTitleColor='blue'
              label={(
                <IconPopoverLabel
                  popoverContent='預設數量為該售價最高可供購買數量，可再下修。'
                >
                  單次限購數量
                </IconPopoverLabel>
              )}
            >
              <Field
                name='listing.cvsPurchaseQtyLimit'
                data-role='listing-cvs-purchase-qty-limit'
                className={styles.cvsPurchaseQtyLimit}
                format={cvsPurchaseQtyLimitFormatter(cvsQtyLimitMax)}
                component={
                  readOnly
                    ? SingleLineText
                    : InputField
                }
                showErrorOnActive={isNewProposal}
                mustShowFormError={mustShowFormError}
              />
            </FormRow>
          )}
        </When>
        <FormRow
          subTitle='(非必填)'
          label={(
            <IconPopoverLabel
              popoverContent='建立主件商品之料號'
            >
              供應商商品料號
            </IconPopoverLabel>
          )}
        >
          <Field
            {...ListingFieldConfig.getField('model.partNo')}
            name='product.partNo'
            component={
              readOnly
                ? SingleLineText
                : InputCountField
            }
            showErrorOnActive={isNewProposal}
            mustShowFormError={mustShowFormError}
          />
        </FormRow>
      </div>
    )
  }
}

Limitation.propTypes = {
  triggerFieldChange: PropTypes.func.isRequired,
  subStationGpm: PropTypes.string.isRequired,
  listingGpm: PropTypes.string.isRequired,
  mustShowFormError: PropTypes.bool,
  readOnly: PropTypes.bool,
  shipTypeId: PropTypes.number,
  user: PropTypes.object.isRequired,
  cvsQtyLimitMax: PropTypes.number,
  proposalType: PropTypes.string.isRequired
}

Limitation.defaultProps = {
  proposalType: PROPOSAL_TYPE.NEW_LISTING
}

export default Limitation
